import React, { useState, useEffect } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { IoChevronDown, IoCheckmarkCircle } from "react-icons/io5";
import WebSearchStep from "./WebSearchStep";
import { adminColors } from "../../theme/adminTheme";

// "Dead air" reassurance tiers: shown only when nothing real (a new backend
// step, or the first answer token) has happened for a while, so the user
// never stares at a completely static label wondering if the chat is stuck.
// Each entry fires `afterMs` after the most recent real update; a real
// update always takes priority and resets the clock.
const REASSURANCE_TIERS = [
  { afterMs: 10000, text: "This is taking a little longer than usual — thanks for your patience." },
  { afterMs: 4000, text: "Still working on it..." },
];

// Claude.ai-style "Thinking" indicator: a shimmering label while the reply
// is being generated, which collapses into "Thought for Xs" the moment the
// first token of the visible answer arrives. If the backend reported live
// progress steps, they're tucked behind the chevron instead of narrated
// permanently - this is a wait-state affordance, not a real reasoning trace.
const ThinkingIndicator = ({ isThinking, label, steps = [], durationMs }) => {
  const [expanded, setExpanded] = useState(false);
  const [reassurance, setReassurance] = useState(null);

  // Steps carrying a `searchMeta` payload (query + real result list) get a
  // persistent, always-visible web-search card instead of being narrated as
  // a single overwritable label - this is the step that was previously
  // getting clobbered by REASSURANCE_TIERS text (the confirmed bug where
  // search never visibly showed anything but "Thinking"/"Still working on
  // it..."). Everything else keeps rendering as plain collapsed step text.
  const searchStep = steps.find((s) => s && typeof s === "object" && s.searchMeta);
  const plainSteps = steps.filter((s) => typeof s === "string");
  const hasDetails = plainSteps.length > 0;
  const seconds = durationMs ? Math.max(1, Math.round(durationMs / 1000)) : null;

  // Reset any reassurance message the moment real progress happens (a new
  // label/step arrives, or thinking starts/stops), then schedule the next
  // tier(s) - so "dead air" only gets filled in after real silence, and is
  // immediately cleared the instant something genuine comes in.
  useEffect(() => {
    setReassurance(null);
    if (!isThinking) return undefined;

    const timers = REASSURANCE_TIERS.map(({ afterMs, text }) =>
      setTimeout(() => setReassurance(text), afterMs)
    );
    return () => timers.forEach(clearTimeout);
  }, [isThinking, label]);

  if (!isThinking && seconds === null) return null;

  // Once a real web-search step exists, reassurance text must never replace
  // it (that's the masking bug) - it can still supplement the label with the
  // latest real step text, just never the generic "dead air" filler.
  const displayLabel = isThinking
    ? (searchStep ? (label || "Thinking") : (reassurance || label || "Thinking"))
    : `Thought for ${seconds}s`;

  return (
    <Box sx={{ mb: isThinking ? 1.5 : 1 }}>
      <Box
        onClick={() => hasDetails && setExpanded((v) => !v)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: hasDetails ? "pointer" : "default",
          userSelect: "none"
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: "13.5px",
            fontWeight: 500,
            ...(isThinking
              ? {
                backgroundImage: `linear-gradient(90deg, ${adminColors.inkSoft} 0%, #e4e4e7 50%, ${adminColors.inkSoft} 100%)`,
                backgroundSize: "200% 100%",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                animation: "thinkingShimmer 1.8s linear infinite",
                "@keyframes thinkingShimmer": {
                  "0%": { backgroundPosition: "200% 0" },
                  "100%": { backgroundPosition: "-200% 0" }
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  backgroundImage: "none",
                  WebkitBackgroundClip: "unset",
                  backgroundClip: "unset",
                  color: adminColors.inkSoft
                }
              }
              : { color: adminColors.inkSoft })
          }}
        >
          {displayLabel}
        </Typography>

        {hasDetails && (
          <IoChevronDown
            size={12}
            color={adminColors.inkSoft}
            style={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease"
            }}
          />
        )}
      </Box>

      {hasDetails && (
        <Collapse in={expanded}>
          <Box sx={{ mt: 1, ml: 0.5, pl: 1.5, borderLeft: "2px solid #e5e7eb" }}>
            {plainSteps.map((step, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ color: adminColors.inkSoft, fontSize: "13px", lineHeight: 1.6, mb: 0.5 }}
              >
                {step}
              </Typography>
            ))}
          </Box>
        </Collapse>
      )}

      {/* Persistent web-search step card - rendered unconditionally once a
          search step has arrived, regardless of reassurance timers or the
          collapsed-details chevron, so it can never be hidden (the
          confirmed bug this feature fixes). Starts in a "pending" state
          (query known, no results yet) and updates in place once the
          search completes. Stays visible after streaming finishes and
          after a page refresh (steps are preserved on the finalized
          message). */}
      {searchStep && (
        <>
          <WebSearchStep
            query={searchStep.searchMeta.query}
            results={searchStep.searchMeta.results}
            pending={!!searchStep.searchMeta.pending}
          />
          {!isThinking && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5, mb: 1 }}>
              <IoCheckmarkCircle size={13} color={adminColors.success} />
              <Typography variant="body2" sx={{ fontSize: "12.5px", color: adminColors.success }}>
                Done
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ThinkingIndicator;
